import { InputValue, WidgetFormSubmissionWithWidget } from "@closerplatform/spinner-openapi";
import { WidgetButton } from "@fidget/common/components/widgetButton/widgetButton";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import {
  isContactEnquiryConfigContext,
  isOplAddressConfigContext,
  isOrdinaryConfigContext
} from "@fidget/common/models/shared/spinner/types/messageWidgets/WidgetFormSubmissionWithWidget";
import { logger } from "@fidget/common/utils";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useForeContext, useStore } from "../../../models";
import { AddressFormChatItem } from "../addressForm/addressFormChatItem";
import { CheckboxListInput } from "./components/checkboxListInput/checkboxListInput";
import { MultipleButtonsInput } from "./components/multipleButtonsInput/multipleButtonsInput";
import { RadioButtonsInput } from "./components/radioButtonsInput/radioButtonsInput";
import { RadioListInput } from "./components/radioListInput/radioListInput";
import { TextInput } from "./components/textInput/textInput";
import { ContactEnquiryForm } from "./contactEnquiryForm/contactEnquiryForm";
import { LimitedWidgetInputProps, UserAnswers, WidgetInput, WidgetInputType } from "./types";
import { prepareAnswersForSubmit } from "./utils";
import {
  ContactEnquiryInputs,
  createContactEnquiryUsersAnswers
} from "./utils/contactEnquiryFormUtils";
import { createOplAddressUsersAnswers } from "./utils/oplAdressFormUtils";
import {
  calculateExpiresIn,
  createUsersAnswers,
  createWidgetInputsFromConfig
} from "./utils/ordinaryFormUtils";

interface Props {
  submission: WidgetFormSubmissionWithWidget;
  messageId: string;
  scrollToBottom?(): void;
}

export const MessageWidgetForm = observer(({ submission, messageId, scrollToBottom }: Props) => {
  const { uiStore } = useStore();
  const { spinnerService, state } = useForeContext();
  const phoneRegion = state.shared.orgConfig?.phoneRegion || "ZZ";

  const [widgetInputs, setWidgetInputs] = useState<WidgetInput[]>(
    isOrdinaryConfigContext(submission.widgetConfig)
      ? createWidgetInputsFromConfig(submission.widgetConfig)
      : []
  );
  const [userAnswers, setUserAnswers] = useState<UserAnswers>(
    createUserAnswersFromSubmission(submission)
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [expireTimeout, setExpireTimeout] = useState<number>();
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      clearTimeout(expireTimeout);
    };
  }, []);

  useEffect(() => {
    const setupFormFromSubmission = () => {
      if (isOrdinaryConfigContext(submission.widgetConfig)) {
        setWidgetInputs(createWidgetInputsFromConfig(submission.widgetConfig));
      }
      setUserAnswers(createUserAnswersFromSubmission(submission));
    };

    setupFormFromSubmission();
    setIsSubmitted(!!submission?.userInputs);

    const expiresIn = calculateExpiresIn(submission);
    if (expiresIn) {
      if (expiresIn <= 0) {
        setIsSubmitted(true);
      } else {
        const timeout = window.setTimeout(() => {
          setIsSubmitted(true);
          setupFormFromSubmission();
        }, expiresIn);
        setExpireTimeout(timeout);
      }
    }
  }, [submission]);

  const checkShouldMakeSubmittable = (updatedAnswers: UserAnswers) => {
    if (isContactEnquiryConfigContext(submission.widgetConfig)) {
      setCanSubmit(
        !!updatedAnswers[ContactEnquiryInputs.Contact]?.value &&
          (!submission.widgetConfig.gdpr.enabled ||
            !!updatedAnswers[ContactEnquiryInputs.Agreement]?.checked)
      );
    } else if (!!widgetInputs.length && widgetInputs.length === size(updatedAnswers)) {
      setCanSubmit(true);
    }
  };

  const chooseAnswer = (inputValue: InputValue) => {
    const updatedAnswers = { ...userAnswers, [inputValue.inputName]: inputValue };
    setUserAnswers(updatedAnswers);
    checkShouldMakeSubmittable(updatedAnswers);
  };

  const renderWidgetInputs = (shared: LimitedWidgetInputProps) => {
    return widgetInputs.map((widgetInput) => {
      switch (widgetInput.type) {
        case WidgetInputType.RadioButtons:
          return <RadioButtonsInput key={widgetInput.name} input={widgetInput} {...shared} />;
        case WidgetInputType.MultipleButtons:
          return <MultipleButtonsInput key={widgetInput.name} input={widgetInput} {...shared} />;
        case WidgetInputType.RadioList:
          return <RadioListInput key={widgetInput.name} input={widgetInput} {...shared} />;
        case WidgetInputType.CheckboxList:
          return <CheckboxListInput key={widgetInput.name} input={widgetInput} {...shared} />;
        case WidgetInputType.Text:
          return <TextInput key={widgetInput.name} input={widgetInput} {...shared} />;
        default:
          assertUnreachable(widgetInput);
          return <></>;
      }
    });
  };

  const renderFormElements = () => {
    const shared: LimitedWidgetInputProps = {
      formId: submission.id,
      userAnswers,
      isSubmitted,
      onChange: chooseAnswer
    };

    if (isOrdinaryConfigContext(submission.widgetConfig)) {
      return renderWidgetInputs(shared);
    }

    if (isOplAddressConfigContext(submission.widgetConfig)) {
      if (isEmpty(userAnswers)) {
        return;
      }

      return <AddressFormChatItem onCanSubmitChange={setCanSubmit} {...shared} />;
    }
    if (isContactEnquiryConfigContext(submission.widgetConfig)) {
      return (
        <ContactEnquiryForm
          {...shared}
          configContext={submission.widgetConfig}
          phoneRegion={phoneRegion}
        />
      );
    }
    logger.error("Cannot render message widget with config: ", submission.widgetConfig);
    return;
  };

  const submit = () => {
    if (submission) {
      if (scrollToBottom) {
        scrollToBottom();
      }

      spinnerService
        .submitWidgetForm(
          submission.id,
          Object.values(prepareAnswersForSubmit(submission, userAnswers, phoneRegion))
        )
        .then((submitted) => {
          const updatedSubmission = {
            ...submitted,
            widgetConfig: submission.widgetConfig,
            name: submission.name
          };
          state.room.chat.setSubmission(messageId, updatedSubmission);
        })
        .catch((e) => {
          logger.error({ message: "could not submit widget form ", error: e });
          uiStore.setChatNotification({ message: intl.formatMessage("widget.submitForm.failed") });
        });
    }
  };

  return (
    <>
      {renderFormElements()}
      <WidgetButton
        disabled={isSubmitted || !canSubmit}
        onClick={submit}
        fullWidth
        buttonStyle="mainOrg"
      >
        <L10n id={"widget.chat.gdpr.save"} />
      </WidgetButton>
    </>
  );
});

const createUserAnswersFromSubmission = (
  submission: WidgetFormSubmissionWithWidget
): UserAnswers => {
  const { widgetConfig } = submission;
  if (isOrdinaryConfigContext(widgetConfig)) {
    return createUsersAnswers(submission, widgetConfig);
  } else if (isOplAddressConfigContext(widgetConfig)) {
    return createOplAddressUsersAnswers(submission);
  } else if (isContactEnquiryConfigContext(widgetConfig)) {
    return createContactEnquiryUsersAnswers(submission);
  } else {
    logger.error("Incorrect config type in message widget", widgetConfig);
    return {};
  }
};
